import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Briefcase, TrendingUp, TrendingDown, DollarSign, BarChart3, PieChart, ArrowRight } from 'lucide-react';
import { tradingAPI } from '../services/api';

const Portfolio = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchPortfolio();
  }, [user]);

  const fetchPortfolio = async () => {
    setLoading(true);
    try {
      const [portRes, tradesRes] = await Promise.all([
        tradingAPI.getPortfolio(),
        tradingAPI.getMyTrades(20),
      ]);
      setPortfolio(portRes.data);
      setTrades(tradesRes.data || []);
    } catch {}
    setLoading(false);
  };

  if (!user) return null;

  const holdings = portfolio?.holdings || [];
  const totalValue = portfolio?.total_value || 0;
  const totalPL = portfolio?.total_profit_loss || 0;
  const cashBalance = user?.cash_balance || 0;
  const netWorth = cashBalance + totalValue;

  return (
    <div className="min-h-screen bg-[#0A0E17]">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-[#F59E0B]/20 rounded-2xl flex items-center justify-center">
            <Briefcase className="w-6 h-6 text-[#F59E0B]" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-medium text-[#F8FAFC]" data-testid="portfolio-title">Portfolio</h1>
            <p className="text-[#94A3B8]">Your trading dashboard</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Net Worth', value: netWorth, icon: DollarSign, color: '#F59E0B' },
            { label: 'Portfolio Value', value: totalValue, icon: PieChart, color: '#8B5CF6' },
            { label: 'Cash Balance', value: cashBalance, icon: BarChart3, color: '#10B981' },
            { label: 'Total P&L', value: totalPL, icon: TrendingUp, color: totalPL >= 0 ? '#10B981' : '#EF4444' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-[#121826] rounded-2xl border border-[#1E293B] p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="flex items-center gap-2 mb-3">
                <Icon className="w-4 h-4" style={{ color }} />
                <span className="text-[#64748B] text-sm">{label}</span>
              </div>
              <span className="text-[#F8FAFC] font-bold text-2xl font-mono">B₹{(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-[#121826] rounded-2xl border border-[#1E293B] animate-pulse" />)}</div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="bg-[#121826] rounded-2xl border border-[#1E293B] overflow-hidden" data-testid="holdings-table">
                <div className="bg-[#1E293B] px-6 py-4 flex items-center justify-between">
                  <h2 className="text-[#F8FAFC] font-bold text-lg font-heading">Holdings ({holdings.length})</h2>
                </div>
                {holdings.length > 0 ? (
                  <div className="divide-y divide-[#1E293B]">
                    {holdings.map((h) => {
                      const pl = h.profit_loss || 0;
                      return (
                        <Link key={h.symbol} to={`/security/${h.symbol}`} className="flex items-center gap-4 px-6 py-4 hover:bg-[#1E293B]/50 transition-colors" data-testid={`holding-${h.symbol}`}>
                          <div className="w-10 h-10 bg-[#1E293B] rounded-lg flex items-center justify-center">
                            <span className="text-[#F59E0B] font-bold text-xs font-mono">{h.symbol?.slice(0,3)}</span>
                          </div>
                          <div className="flex-1">
                            <div className="text-[#F8FAFC] font-semibold">{h.name || h.symbol}</div>
                            <div className="text-[#64748B] text-sm">{h.shares} shares @ B₹{(h.average_price || 0).toFixed(2)}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-[#F8FAFC] font-bold font-mono">B₹{(h.current_value || 0).toFixed(0)}</div>
                            <div className={`text-sm font-mono ${pl >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                              {pl >= 0 ? '+' : ''}B₹{pl.toFixed(0)}
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-[#64748B]" />
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <div className="px-6 py-12 text-center">
                    <Briefcase className="w-10 h-10 text-[#64748B] mx-auto mb-3" />
                    <p className="text-[#94A3B8] mb-4">No holdings yet</p>
                    <Link to="/securities"><Button className="bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold" data-testid="start-trading-btn">Start Trading</Button></Link>
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="bg-[#121826] rounded-2xl border border-[#1E293B] overflow-hidden" data-testid="recent-trades-panel">
                <div className="bg-[#1E293B] px-4 py-3">
                  <h3 className="text-[#F8FAFC] font-bold font-heading">Recent Trades</h3>
                </div>
                <div className="divide-y divide-[#1E293B]">
                  {trades.length > 0 ? trades.slice(0, 10).map((t, i) => (
                    <div key={i} className="px-4 py-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[#F59E0B] font-mono text-sm font-bold">{t.symbol}</span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${t.trade_type === 'buy' ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#EF4444]/20 text-[#EF4444]'}`}>
                          {t.trade_type?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-[#64748B]">
                        <span>{t.shares} shares</span>
                        <span className="font-mono">B₹{(t.price || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  )) : (
                    <div className="px-4 py-8 text-center text-[#64748B] text-sm">No trades yet</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Portfolio;
